import { useState, useEffect } from 'react';
import type { RankEntry } from '../../types';

interface Props {
  financialRanking: RankEntry[];
  emotionalRanking: RankEntry[];
  combinedRanking: RankEntry[];
  decisionConfidence: number;
  financialWeight?: number;
  emotionalWeight?: number;
  onLiveRankingChange?: (ranking: RankEntry[], financialWeight: number, emotionalWeight: number) => void;
}

function fmt$(v: number): string {
  return '$' + v.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function MedalBadge({ isWinner }: { isWinner: boolean }) {
  if (isWinner) {
    return (
      <span
        className="inline-flex items-center justify-center w-6 h-6 rounded-full text-xs font-bold"
        style={{
          background: 'var(--tco-accent-soft)',
          color: 'var(--tco-accent)',
          border: '1px solid var(--tco-accent)',
        }}
      >
        <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 20 20">
          <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
        </svg>
      </span>
    );
  }
  return (
    <span
      className="inline-flex items-center justify-center w-6 h-6 rounded-full text-xs font-bold"
      style={{ background: 'var(--tco-line-soft)', color: 'var(--tco-ink-3)' }}
    >
      —
    </span>
  );
}

interface FormulaEntry {
  finScore: number;
  effEmoScore: number;
  finWeight: number;
  emoWeight: number;
}

interface RankColumnProps {
  title: string;
  entriesInOrder: RankEntry[];
  winnerVehicleId: string | undefined;
  scoreFormat: 'currency' | 'score';
  scoreMap: Map<string, number>;
  rankMap: Map<string, number>;
  formulaMap?: Map<string, FormulaEntry>;
}

function RankColumn({
  title,
  entriesInOrder,
  winnerVehicleId,
  scoreFormat,
  scoreMap,
  rankMap,
  formulaMap,
}: RankColumnProps) {
  return (
    <div
      className="rounded-xl p-4 flex flex-col gap-3"
      style={{ background: 'var(--tco-surface)', border: '1px solid var(--tco-line)' }}
    >
      <p
        className="text-xs font-semibold uppercase"
        style={{ color: 'var(--tco-ink-3)', fontFamily: 'var(--tco-font-sans)', letterSpacing: '0.18em' }}
      >
        {title}
      </p>
      <ol className="flex flex-col gap-2">
        {entriesInOrder.map((e) => {
          const isWinner = e.vehicle_id === winnerVehicleId;
          const score = scoreMap.get(e.vehicle_id) ?? 0;
          const dimRank = rankMap.get(e.vehicle_id) ?? 0;
          const formula = formulaMap?.get(e.vehicle_id);
          return (
            <li
              key={e.vehicle_id}
              className="flex items-center justify-between gap-2 rounded-lg px-3 py-2"
              style={
                isWinner
                  ? {
                      background: 'var(--tco-accent-soft)',
                      border: '1px solid var(--tco-accent)',
                      borderLeft: '3px solid var(--tco-accent)',
                    }
                  : { background: 'var(--tco-line-soft)', border: '1px solid var(--tco-line)' }
              }
            >
              <div className="flex items-center gap-2 min-w-0">
                <MedalBadge isWinner={isWinner} />
                <span className="text-sm truncate" style={{ color: 'var(--tco-ink)' }} title={e.label}>
                  {e.label}
                </span>
              </div>
              <div className="flex flex-col items-end shrink-0">
                <span className="text-xs font-mono" style={{ color: 'var(--tco-ink-2)' }}>
                  {scoreFormat === 'currency'
                    ? fmt$(score)
                    : (score * 100).toFixed(1)}
                </span>
                {formula && (
                  <span className="text-xs font-mono leading-tight" style={{ color: 'var(--tco-ink-3)' }}>
                    {(formula.finScore * 100).toFixed(1)}×{Math.round(formula.finWeight * 100)}%+{(formula.effEmoScore * 100).toFixed(1)}×{Math.round(formula.emoWeight * 100)}%
                  </span>
                )}
                {!isWinner && (
                  <span className="text-xs" style={{ color: 'var(--tco-ink-3)' }}>#{dimRank}</span>
                )}
              </div>
            </li>
          );
        })}
      </ol>
    </div>
  );
}

export default function RankingPanel({
  financialRanking,
  emotionalRanking,
  combinedRanking: _combinedRanking,
  financialWeight: initialFinancialWeight = 0.70,
  emotionalWeight: _initialEmotionalWeight = 0.30,
  onLiveRankingChange,
}: Props) {
  // Live weight slider state — starts from scenario-level settings
  const [liveFinancialPct, setLiveFinancialPct] = useState(Math.round(initialFinancialWeight * 100));
  const liveFinancialWeight = liveFinancialPct / 100;
  const liveEmotionalWeight = 1 - liveFinancialWeight;

  // Build score and rank maps for each dimension
  const financialScoreMap = new Map(financialRanking.map((e) => [e.vehicle_id, e.score]));
  const emotionalScoreMap = new Map(emotionalRanking.map((e) => [e.vehicle_id, e.score]));

  const financialRankMap = new Map(financialRanking.map((e) => [e.vehicle_id, e.rank]));
  const emotionalRankMap = new Map(emotionalRanking.map((e) => [e.vehicle_id, e.rank]));

  // Normalize financial scores: ratio formula matches backend emotional.py
  const tcoValues = [...financialScoreMap.values()];
  const minTco = Math.min(...tcoValues);

  // Recalculate combined scores using live weights — emotional always contributes
  const liveCombinedEntries: { vehicle_id: string; label: string; combined: number }[] =
    financialRanking.map((e) => {
      const tco = financialScoreMap.get(e.vehicle_id) ?? 0;
      const finScore = tco > 1e-9 ? minTco / tco : 1.0;
      const emoScore = emotionalScoreMap.get(e.vehicle_id) ?? 0;
      const combined = liveFinancialWeight * finScore + liveEmotionalWeight * emoScore;
      return { vehicle_id: e.vehicle_id, label: e.label, combined };
    });

  const liveSorted = [...liveCombinedEntries].sort((a, b) => b.combined - a.combined);
  const liveCombinedRanking: RankEntry[] = liveSorted.map((e, i) => ({
    vehicle_id: e.vehicle_id,
    label: e.label,
    rank: i + 1,
    score: Math.round(e.combined * 10000) / 10000,
  }));

  const combinedScoreMap = new Map(liveCombinedRanking.map((e) => [e.vehicle_id, e.score]));
  const combinedRankMap = new Map(liveCombinedRanking.map((e) => [e.vehicle_id, e.rank]));

  // Display order is determined by live combined score
  const combinedOrder = [...liveCombinedRanking].sort((a, b) => a.rank - b.rank);

  const financialWinner = financialRanking.find((e) => e.rank === 1)?.vehicle_id;
  const emotionalWinner = emotionalRanking.find((e) => e.rank === 1)?.vehicle_id;
  const combinedWinner = liveCombinedRanking.find((e) => e.rank === 1)?.vehicle_id;

  // Build formula breakdown map for Combined column (shows fin/emo component scores)
  const formulaMap = new Map<string, FormulaEntry>(
    financialRanking.map((e) => {
      const tco = financialScoreMap.get(e.vehicle_id) ?? 0;
      const finScore = tco > 1e-9 ? minTco / tco : 1.0;
      const emoScore = emotionalScoreMap.get(e.vehicle_id) ?? 0;
      return [e.vehicle_id, {
        finScore,
        effEmoScore: emoScore,
        finWeight: liveFinancialWeight,
        emoWeight: liveEmotionalWeight,
      }];
    })
  );

  // Notify parent of live combined ranking whenever slider position changes
  useEffect(() => {
    onLiveRankingChange?.(liveCombinedRanking, liveFinancialWeight, liveEmotionalWeight);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [liveFinancialPct]);

  return (
    <div className="flex flex-col gap-4">
      {/* Live Decision Weight Slider */}
      <div
        className="rounded-xl px-5 py-4 flex flex-col gap-3"
        style={{ background: 'var(--tco-surface)', border: '1px solid var(--tco-line)' }}
      >
        <p
          className="text-xs font-semibold uppercase"
          style={{ color: 'var(--tco-ink-3)', fontFamily: 'var(--tco-font-sans)', letterSpacing: '0.18em' }}
        >
          Adjust Decision Weight
        </p>
        <div className="flex justify-between text-xs mb-1" style={{ color: 'var(--tco-ink)' }}>
          <span>Financial</span>
          <span className="font-semibold" style={{ color: 'var(--tco-ink)' }}>
            Financial {liveFinancialPct}% / Emotional {100 - liveFinancialPct}%
          </span>
          <span>Emotional</span>
        </div>
        <input
          type="range"
          min={0}
          max={100}
          step={5}
          value={liveFinancialPct}
          onChange={(e) => setLiveFinancialPct(Number(e.target.value))}
          className="w-full h-1.5 rounded cursor-pointer"
          style={{ accentColor: 'var(--tco-ink)' }}
        />
        <div className="flex justify-between text-xs" style={{ color: 'var(--tco-ink-3)' }}>
          <span>0% Financial</span>
          <span>100% Financial</span>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <RankColumn
          title="Financial Ranking"
          entriesInOrder={combinedOrder}
          winnerVehicleId={financialWinner}
          scoreFormat="currency"
          scoreMap={financialScoreMap}
          rankMap={financialRankMap}
        />
        <RankColumn
          title="Emotional Ranking"
          entriesInOrder={combinedOrder}
          winnerVehicleId={emotionalWinner}
          scoreFormat="score"
          scoreMap={emotionalScoreMap}
          rankMap={emotionalRankMap}
        />
        <RankColumn
          title="Combined Ranking (Live)"
          entriesInOrder={combinedOrder}
          winnerVehicleId={combinedWinner}
          scoreFormat="score"
          scoreMap={combinedScoreMap}
          rankMap={combinedRankMap}
          formulaMap={formulaMap}
        />
      </div>

    </div>
  );
}
